import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  AlertTriangle,
  ArrowLeft,
  Check as CheckIcon,
  XCircle,
} from "lucide-react";
import {
  MapContainer,
  Marker,
  Polyline,
  TileLayer,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { getVisitById } from "../config/url";
import { getConfigWithToken } from "../config/config";
import formatPhoneNumber from "../utils/formatPhoneNumber";
import {
  formatCoordinateDegreesLabel,
  formatDurationLabel,
  formatFlagLabel,
  formatTimeUS,
  formatVisitDate,
  formatVisitDisplayId,
  getClientName,
  getClientPhoneNumber,
  getCompletedInterventions,
  getConfirmationStatus,
  getCoordinatePair,
  getFlagCount,
  getFlagReasons,
  getGpsAccuracyLabel,
  getGpsAlertMessage,
  getGpsDisplayLabel,
  getGpsDistanceLabel,
  getGpsMissingState,
  getProviderName,
  getVisitStatus,
  isFlaggedVisit,
} from "../utils/visitDisplay";
import ClientConfirmationPanel from "../components/ClientConfirmationPanel";
import classes from "./VisitDetails.module.css";

const BRAND = "#064260";

const normalizeVisitResponse = (resData) => {
  const payload = resData?.data ?? resData;
  if (Array.isArray(payload)) return payload[0] ?? null;
  return payload ?? null;
};

const formatPhoneValue = (value) => {
  if (!value) return "—";
  try {
    return formatPhoneNumber(String(value)) || value;
  } catch {
    return value;
  }
};

const STATUS_CLASS = {
  Verified: classes.statusVerified,
  Flagged: classes.statusFlaggedHeader,
  Incomplete: classes.statusIncomplete,
};

const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (!bounds || bounds.length === 0) return;
    map.fitBounds(bounds, { padding: [30, 30] });
  }, [bounds, map]);
  return null;
};

const DetailItem = ({ isNode = false, label, value, valueClassName = "" }) => (
  <div className={classes.detailItem}>
    <span className={classes.detailLabel}>{label}</span>
    {isNode ? (
      <div className={classes.detailValueNode}>{value}</div>
    ) : (
      <span className={`${classes.detailValue} ${valueClassName}`.trim()}>
        {value}
      </span>
    )}
  </div>
);

const VisitDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state?.user);
  const token = currentUser?.user?.token || localStorage.getItem("user_id");
  const organizationId =
    currentUser?.user?.organizationId ||
    localStorage.getItem("organizationId") ||
    localStorage.getItem("businessId") ||
    "";

  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);

  const requestConfig = useMemo(
    () => ({
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(token ? getConfigWithToken(token).headers : {}),
        ...(organizationId ? { "x-organization-id": organizationId } : {}),
      },
      params: organizationId ? { organizationId } : {},
    }),
    [organizationId, token],
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(getVisitById(id), requestConfig);
        if (!cancelled) {
          setVisit(normalizeVisitResponse(res.data));
        }
      } catch (err) {
        if (!cancelled) {
          setVisit(null);
          toast.error(err?.response?.data?.message || "Failed to load visit.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id, requestConfig]);

  const mapIcons = useMemo(
    () => ({
      start: L.divIcon({
        className: classes.markerWrap,
        html: `<div class="${classes.labeledMarker} ${classes.labeledMarkerStart}"><span class="${classes.labeledMarkerDot}"></span><span class="${classes.labeledMarkerText}">Start</span></div>`,
        iconAnchor: [24, 42],
        iconSize: [48, 48],
      }),
      end: L.divIcon({
        className: classes.markerWrap,
        html: `<div class="${classes.labeledMarker} ${classes.labeledMarkerEnd}"><span class="${classes.labeledMarkerDot}"></span><span class="${classes.labeledMarkerText}">End</span></div>`,
        iconAnchor: [24, 42],
        iconSize: [48, 48],
      }),
    }),
    [],
  );

  if (loading) {
    return (
      <div className={classes.page}>
        <div className={classes.stateCard}>
          <Spinner animation="border" style={{ color: BRAND }} />
          <div className={classes.stateText}>Loading visit...</div>
        </div>
      </div>
    );
  }

  if (!visit) {
    return (
      <div className={classes.page}>
        <div className={classes.stateCard}>
          <p className={classes.stateText}>No visit found.</p>
          <button
            className={classes.outlineButton}
            onClick={() => navigate("/visits")}
            type="button"
          >
            Back to Community Visits
          </button>
        </div>
      </div>
    );
  }

  const visitStatus = getVisitStatus(visit);
  const flagged = isFlaggedVisit(visit);
  const confirmationStatus = getConfirmationStatus(visit) || "—";
  const gpsDisplayLabel = getGpsDisplayLabel(visit);
  const interventions = getCompletedInterventions(visit);
  const flagReasons = getFlagReasons(visit);
  const flagCount = getFlagCount(visit);
  const { start, end } = getCoordinatePair(visit.location);
  const gpsMissing = getGpsMissingState(visit);
  const gpsAlertMessage = getGpsAlertMessage(visit);
  const hasStart = gpsMissing.hasStart;
  const hasEnd = gpsMissing.hasEnd;
  const showMap = hasStart || hasEnd;
  const mapBounds = [
    ...(hasStart ? [[start.lat, start.lng]] : []),
    ...(hasEnd ? [[end.lat, end.lng]] : []),
  ];

  const successValueClass = classes.detailValueSuccess;
  const warningValueClass = classes.detailValueWarning;
  const dangerValueClass = classes.detailValueDanger;
  const mutedValueClass = classes.detailValueMuted;

  const getStatusValueClass = (value) => {
    if (value === "Verified" || value === "Valid" || value === "Confirmed")
      return successValueClass;
    if (value === "Missing") return dangerValueClass;
    if (value === "Flagged" || value === "Invalid" || value === "Unconfirmed")
      return warningValueClass;
    return mutedValueClass;
  };

  const renderStatusBadge = (status) => {
    if (!status || status === "—") return "—";
    const badgeClass = STATUS_CLASS[status] || classes.statusIncomplete;
    return (
      <span className={`${badgeClass} ${classes.statusBadgeSmall}`}>
        <span className={classes.statusDot} />
        {status}
      </span>
    );
  };

  return (
    <div
      className={`${classes.page} ${flagged ? classes.pageFlagged : ""}`.trim()}
    >
      <div className={classes.pageTop}>
        <button
          aria-label="Back to Community Visits"
          className={classes.backButton}
          onClick={() => navigate("/visits")}
          type="button"
        >
          <ArrowLeft color="#ffffff" size={18} strokeWidth={2.25} />
        </button>
        <h1 className={classes.pageTitle}>Visit Details</h1>
      </div>

      <div
        className={`${classes.visitIdCard} ${flagged ? classes.visitIdCardFlagged : ""}`.trim()}
      >
        <div className={classes.visitIdBlock}>
          <div className={classes.visitIdLabel}>Visit ID</div>
          <div className={classes.visitIdValue}>
            {formatVisitDisplayId(visit)}
          </div>
        </div>
        <div aria-hidden="true" className={classes.visitIdDivider} />
        {flagged ? (
          <div className={classes.visitIdBadges}>
            <span className={classes.statusFlaggedHeader}>
              <span className={classes.statusDot} />
              Flagged
            </span>
            {flagCount > 0 ? (
              <span className={classes.flagCountBadge}>
                <span className={classes.statusDot} />
                {formatFlagLabel(visit)}
              </span>
            ) : null}
          </div>
        ) : (
          <div className={classes.visitIdBadges}>
            <span
              className={STATUS_CLASS[visitStatus] || classes.statusIncomplete}
            >
              <span className={classes.statusDot} />
              {visitStatus}
            </span>
          </div>
        )}
      </div>

      {flagged && flagReasons.length > 0 ? (
        <section className={classes.flagsSection}>
          <h2 className={classes.flagsTitle}>Flags</h2>
          <ul className={classes.flagsList}>
            {flagReasons.map((reason) => (
              <li className={classes.flagItem} key={reason}>
                <AlertTriangle
                  className={classes.flagIcon}
                  size={15}
                  strokeWidth={2.25}
                />
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className={classes.sectionStack}>
        <section className={classes.sectionCard}>
          <h2 className={classes.sectionTitle}>Visit Details</h2>
          <div className={classes.detailsGrid}>
            <DetailItem label="Client" value={getClientName(visit)} />
            <DetailItem label="Provider" value={getProviderName(visit)} />
            <DetailItem label="Date" value={formatVisitDate(visit.date)} />
            <DetailItem label="Start" value={formatTimeUS(visit.time_in)} />
            <DetailItem label="End" value={formatTimeUS(visit.time_out)} />
            <DetailItem label="Duration" value={formatDurationLabel(visit)} />
            <DetailItem
              isNode
              label="Status"
              value={renderStatusBadge(visitStatus)}
            />
            <DetailItem
              label="GPS"
              value={gpsDisplayLabel}
              valueClassName={getStatusValueClass(gpsDisplayLabel)}
            />
            <DetailItem
              label="Confirmation"
              value={confirmationStatus}
              valueClassName={getStatusValueClass(confirmationStatus)}
            />
          </div>
        </section>

        <section className={classes.sectionCard}>
          <h2 className={classes.sectionTitle}>Client Confirmation</h2>
          <ClientConfirmationPanel
            flagged={flagged}
            phoneNumber={formatPhoneValue(getClientPhoneNumber(visit))}
            styles={classes}
            verifiedAtFallback={
              flagged ? visit?.time_out || visit?.updated_at : visit?.time_out
            }
            visit={visit}
          />
        </section>

        <section className={classes.sectionCard}>
          <h2 className={classes.sectionTitle}>GPS Information</h2>
          {gpsAlertMessage ? (
            <div
              className={`${classes.gpsAlert} ${showMap ? classes.gpsPartialAlert : ""}`.trim()}
              role="alert"
            >
              <XCircle
                className={classes.gpsAlertIcon}
                size={18}
                strokeWidth={2.25}
              />
              <span>{gpsAlertMessage}</span>
            </div>
          ) : null}

          {showMap ? (
            <div className={classes.mapContainer}>
              <MapContainer
                center={[
                  hasStart ? start.lat : end.lat,
                  hasStart ? start.lng : end.lng,
                ]}
                scrollWheelZoom={false}
                style={{ width: "100%", height: 340 }}
                zoom={13}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds bounds={mapBounds} />
                {hasStart ? (
                  <Marker
                    icon={mapIcons.start}
                    position={[start.lat, start.lng]}
                  />
                ) : null}
                {hasEnd ? (
                  <Marker icon={mapIcons.end} position={[end.lat, end.lng]} />
                ) : null}
                {hasStart && hasEnd ? (
                  <Polyline
                    pathOptions={{ color: "#27b99a", opacity: 0.7, weight: 3 }}
                    positions={[
                      [start.lat, start.lng],
                      [end.lat, end.lng],
                    ]}
                  />
                ) : null}
              </MapContainer>
            </div>
          ) : null}

          {showMap ? (
            <div className={classes.gpsMetaGrid}>
              <DetailItem
                label="Start Coords"
                value={formatCoordinateDegreesLabel(visit, "start")}
              />
              <DetailItem
                label="End Coords"
                value={formatCoordinateDegreesLabel(visit, "end")}
              />
              <DetailItem label="Accuracy" value={getGpsAccuracyLabel(visit)} />
              <DetailItem label="Distance" value={getGpsDistanceLabel(visit)} />
            </div>
          ) : null}
        </section>

        <section className={classes.sectionCard}>
          <h2 className={classes.sectionTitle}>Completed Interventions</h2>
          {interventions.length ? (
            <div className={classes.interventionList}>
              {interventions.map((item, index) => (
                <div
                  className={classes.interventionTag}
                  key={`${item}-${index}`}
                >
                  <span className={classes.interventionIconWrap}>
                    <CheckIcon size={12} strokeWidth={3} />
                  </span>
                  {item}
                </div>
              ))}
            </div>
          ) : (
            <div className={classes.emptyState}>
              No completed interventions recorded.
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default VisitDetails;
